/**
 * Админ-API: LLM-провайдеры, глобальные настройки ИИ, определения агентов.
 */

import { apiRequest } from "@/lib/api-client-v2";
import type {
  AgentDashboardStatsRead,
  AgentDefinitionRead,
  AgentRunsPage,
  AiGlobalSettingsRead,
  LlmProviderCreatePayload,
  LlmProviderKindRead,
  LlmProviderRead,
  LlmProviderUpdatePayload,
  LlmUsageSummaryRead,
  TestConnectionResult,
} from "@/types/ai-admin";

const BASE = "/ai-admin";

export const aiAdminApi = {
  listLlmProviderKinds: () => apiRequest<LlmProviderKindRead[]>(`${BASE}/llm-provider-kinds`),

  listLlmProviders: () => apiRequest<LlmProviderRead[]>(`${BASE}/llm-providers`),

  createLlmProvider: (data: LlmProviderCreatePayload) =>
    apiRequest<LlmProviderRead>(`${BASE}/llm-providers`, { method: "POST", data }),

  updateLlmProvider: (id: number, data: LlmProviderUpdatePayload) =>
    apiRequest<LlmProviderRead>(`${BASE}/llm-providers/${id}`, { method: "PUT", data }),

  deleteLlmProvider: (id: number) =>
    apiRequest<void>(`${BASE}/llm-providers/${id}`, { method: "DELETE" }),

  testLlmConnection: (id: number) =>
    apiRequest<TestConnectionResult>(`${BASE}/llm-providers/${id}/test-connection`, { method: "POST" }),

  getLlmUsageSummary: (id: number, days = 30) =>
    apiRequest<LlmUsageSummaryRead>(`${BASE}/llm-providers/${id}/usage-summary?days=${days}`),

  getGlobalSettings: () => apiRequest<AiGlobalSettingsRead>(`${BASE}/global-settings`),

  patchGlobalSettings: (data: { default_llm_provider_id?: number | null; feature_flags?: Record<string, unknown> }) =>
    apiRequest<AiGlobalSettingsRead>(`${BASE}/global-settings`, { method: "PATCH", data }),

  listAgentDefinitions: () => apiRequest<AgentDefinitionRead[]>(`${BASE}/agent-definitions`),

  /** Тело PATCH — snake_case, как в Pydantic (``AgentDefinitionUpdate``). */
  patchAgentDefinition: (
    id: number,
    data: {
      enabled?: boolean;
      system_prompt_override?: string | null;
      tool_policy?: Record<string, unknown>;
    },
  ) => apiRequest<AgentDefinitionRead>(`${BASE}/agent-definitions/${id}`, { method: "PATCH", data }),

  listAgentRuns: (params: {
    limit?: number;
    offset?: number;
    agent_code?: string;
    status?: string;
    days?: number;
  }) => {
    const q = new URLSearchParams();
    if (params.limit != null) q.set("limit", String(params.limit));
    if (params.offset != null) q.set("offset", String(params.offset));
    if (params.agent_code) q.set("agent_code", params.agent_code);
    if (params.status) q.set("status", params.status);
    if (params.days != null) q.set("days", String(params.days));
    const qs = q.toString();
    return apiRequest<AgentRunsPage>(`${BASE}/agent-runs${qs ? `?${qs}` : ""}`);
  },

  getAgentDashboardStats: (days = 7) =>
    apiRequest<AgentDashboardStatsRead>(`${BASE}/agent-dashboard-stats?days=${days}`),

  setAgentDefaultProvider: (agentId: number, llmProviderId: number) =>
    apiRequest<AgentDefinitionRead>(`${BASE}/agent-definitions/${agentId}/default-provider`, {
      method: "PUT",
      data: { llm_provider_id: llmProviderId },
    }),
};
